import { useEffect } from 'react';
import type { InvestorOption } from '@dealstack/frontend/shared/components/FieldBinding/fieldBinding.types.ts';

interface Args {
	enabled: boolean;
	options: InvestorOption[];
	value: string | null;
	onChange: (investorId: string) => void;
}

// Input types with no caret, so an arrow key means nothing to the control. The preview's own
// picker is one: not searchable, so Mantine marks its input readOnly.
const CARETLESS_INPUT_TYPES = new Set([
	'button',
	'checkbox',
	'color',
	'file',
	'image',
	'radio',
	'range',
	'reset',
	'submit',
]);

/** Inside a region whose own controls use the arrow keys, such as the rule preview's answers. */
const isInShortcutFreeRegion = (target: EventTarget | null): boolean =>
	target instanceof Element && target.closest('[data-preview-shortcuts="off"]') !== null;

const isTextEntry = (target: EventTarget | null): boolean => {
	if (!(target instanceof HTMLElement)) {
		return false;
	}
	if (target.isContentEditable || target instanceof HTMLTextAreaElement) {
		return true;
	}
	if (target instanceof HTMLInputElement) {
		return !target.readOnly && !CARETLESS_INPUT_TYPES.has(target.type);
	}
	return false;
};

/**
 * ← / → step the previewed recipient, so a document can be checked against one investor after
 * another without going back through the picker each time.
 *
 * The default is always suppressed while this is on, even at the ends of the list: the view
 * toggle beside the picker is a radio group, and the browser's own arrow handling for one would
 * otherwise flip the editor back out of the preview whenever the toggle held focus.
 */
export const usePreviewInvestorShortcuts = ({ enabled, options, value, onChange }: Args): void => {
	useEffect(() => {
		if (!enabled) {
			return;
		}
		const onKeyDown = (event: KeyboardEvent): void => {
			if (event.key !== 'ArrowRight' && event.key !== 'ArrowLeft') {
				return;
			}
			const step = event.key === 'ArrowRight' ? 1 : -1;
			if (
				event.repeat ||
				event.altKey ||
				event.ctrlKey ||
				event.metaKey ||
				event.shiftKey ||
				isTextEntry(event.target) ||
				isInShortcutFreeRegion(event.target)
			) {
				return;
			}
			event.preventDefault();
			const index = options.findIndex((option) => option.value === value);
			const nextIndex = index + step;
			// No wrap: stepping stops at the ends rather than starting the round again.
			if (index === -1 || nextIndex < 0) {
				return;
			}
			const next = options.at(nextIndex);
			if (next === undefined) {
				return;
			}
			onChange(next.value);
		};
		document.addEventListener('keydown', onKeyDown);
		return () => document.removeEventListener('keydown', onKeyDown);
	}, [enabled, options, value, onChange]);
};
